/**
 * Clinic portal → Payments (Stripe Connect status)
 *
 * Shows the clinic's own Connect onboarding state, with a "Continue
 * onboarding" / "Resume onboarding" CTA that opens the Stripe-hosted
 * Express onboarding flow in a new tab.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ExternalLink, Loader2, RefreshCw, ShieldCheck, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import PageShell from "@/components/PageShell";

const PILL = {
  active: { cls: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200", icon: CheckCircle2, label: "Active" },
  onboarding: { cls: "bg-amber-500/15 border-amber-500/40 text-amber-200", icon: ShieldCheck, label: "Onboarding" },
  rejected: { cls: "bg-rose-500/15 border-rose-500/40 text-rose-200", icon: XCircle, label: "Rejected" },
  none: { cls: "bg-slate-700/40 border-slate-600/40 text-slate-300", icon: ShieldCheck, label: "Not onboarded" },
};

function pickStatus(state) {
  if (!state.stripe_connect_account_id) return "none";
  if (state.charges_enabled && state.payouts_enabled) return "active";
  if (state.stripe_connect_status === "rejected") return "rejected";
  return "onboarding";
}

export default function PortalPayments() {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/portal/stripe-connect/me");
      setState(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onboard = async () => {
    setActioning("onboarding");
    try {
      const origin = window.location.origin;
      const r = await api.post("/portal/stripe-connect/onboard", {
        // user_id is overridden server-side to the authenticated user.
        user_id: "self",
        return_url: `${origin}/portal/payments?onboarded=1`,
        refresh_url: `${origin}/portal/payments?refresh=1`,
      });
      window.open(r.data.onboarding_url, "_blank", "noopener,noreferrer");
      toast.success("Opened Stripe onboarding in a new tab.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActioning(null);
    }
  };

  const statusKey = state ? pickStatus(state) : "none";
  const pill = PILL[statusKey];
  const PillIcon = pill.icon;

  return (
    <PageShell
      eyebrow="Clinic · Payments"
      title="Stripe Connect"
      intro="Onboard to Stripe Connect to receive patient payments through Nodera. Once active, patient checkouts for your flagship programs will route funds directly into your Stripe account, with platform fees and vendor splits handled automatically."
      testId="portal-payments-page"
    >
      <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-6">
        {loading ? (
          <div className="text-sm text-slate-500 flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                  Current status
                </div>
                <span
                  data-testid="portal-payments-status-pill"
                  className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${pill.cls}`}
                >
                  <PillIcon className="w-3 h-3" />
                  {pill.label}
                </span>
                {state?.stripe_connect_account_id && (
                  <div className="mt-2 text-[11px] text-slate-500 font-mono">
                    Account: {state.stripe_connect_account_id}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2">
                {state?.stripe_connect_account_id && (
                  <button
                    type="button"
                    onClick={load}
                    data-testid="portal-payments-refresh"
                    className="inline-flex items-center gap-1 rounded-full bg-slate-800 border border-slate-700 text-xs font-semibold px-3 py-1.5 text-slate-200 hover:bg-slate-700"
                  >
                    <RefreshCw className="w-3.5 h-3.5" /> Refresh
                  </button>
                )}
                <button
                  type="button"
                  onClick={onboard}
                  disabled={!!actioning}
                  data-testid="portal-payments-onboard"
                  className="inline-flex items-center gap-1 rounded-full bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 hover:bg-blue-700 disabled:opacity-50"
                >
                  {actioning === "onboarding" ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <ExternalLink className="w-3.5 h-3.5" />
                  )}
                  {state?.stripe_connect_account_id ? "Continue onboarding" : "Start onboarding"}
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                  Card payments
                </div>
                <div className="text-sm font-semibold text-slate-100 flex items-center gap-1">
                  {state?.charges_enabled ? (
                    <><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Enabled</>
                  ) : (
                    <><XCircle className="w-4 h-4 text-slate-500" /> Disabled</>
                  )}
                </div>
              </div>
              <div className="rounded-lg border border-slate-800 bg-slate-950/60 p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">
                  Payouts to your bank
                </div>
                <div className="text-sm font-semibold text-slate-100 flex items-center gap-1">
                  {state?.payouts_enabled ? (
                    <><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Enabled</>
                  ) : (
                    <><XCircle className="w-4 h-4 text-slate-500" /> Disabled</>
                  )}
                </div>
              </div>
            </div>

            {statusKey !== "active" && (
              <div className="mt-5 rounded-lg border border-amber-500/30 bg-amber-950/20 p-4 text-xs text-amber-200/85 flex items-start gap-2">
                <ShieldCheck className="w-4 h-4 text-amber-400 mt-0.5 flex-none" />
                <div>
                  <div className="font-semibold text-amber-200 mb-1">
                    {statusKey === "none"
                      ? "You haven't started Stripe onboarding yet."
                      : "Your Stripe onboarding isn't finished."}
                  </div>
                  Patient checkouts for your flagship programs will be rejected until card
                  payments are enabled. Click <span className="font-semibold">Start onboarding</span> to complete it now — it takes about 5 minutes.
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </PageShell>
  );
}
